import { auth, useAuth } from '@common/auth/use-auth';
import { getBootstrapData } from '@ui/bootstrap-data/bootstrap-data-store';
import { useEffect, useMemo } from 'react';
import { Permission } from './permission';

export type Roles = Record<string, any>;
export type Access = Record<string, any>;

export interface useUserRolesAccess {
  isAdmin: boolean,
  userRoles: Roles,
  userAccess: Access
}

function hasDefinedRestrictionValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

function parseRestrictionBool(value: unknown): boolean {
  if (value == null || value === '') {
    return true;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    return !['false', '0', 'off', 'no'].includes(normalized);
  }
  return Boolean(value);
}

function resolveCanQueueFullAlbum(
  isAdmin: boolean,
  getRestrictionValue: (
    permissionName: string,
    restrictionName: string,
  ) => string | number | boolean | undefined | null,
): boolean {
  if (isAdmin) {
    return true;
  }

  const explicitFullAlbum = getRestrictionValue(
    'medias.play',
    'can_queue_full_album',
  );
  if (hasDefinedRestrictionValue(explicitFullAlbum)) {
    return parseRestrictionBool(explicitFullAlbum);
  }

  // Backward compatibility with legacy "queue_auto_mix":
  // true meant "force mix", so full album control was false.
  const legacyAutoMix = getRestrictionValue('medias.play', 'queue_auto_mix');
  if (hasDefinedRestrictionValue(legacyAutoMix)) {
    return !parseRestrictionBool(legacyAutoMix);
  }

  return true;
}

export function useUserRolesAccess(): useUserRolesAccess {
  const {user, hasPermission, getRestrictionValue} = useAuth();
  
  const isAdmin = useMemo(() => { return hasPermission('admin'); }, [user, hasPermission]);

  const userRoles: Roles = useMemo(() => {
    return {
      admins: isAdmin || ['admins'].some(a => user?.roles.find(b => b.name === a)) || false,
      users: isAdmin || ['users'].some(a => user?.roles.find(b => b.name === a)) || false,
      artists: isAdmin || ['artists'].some(a => user?.roles.find(b => b.name === a)) || false,
      creators: isAdmin || ['creators'].some(a => user?.roles.find(b => b.name === a)) || false,
      authors: isAdmin || ['authors'].some(a => user?.roles.find(b => b.name === a)) || false,
      labels: isAdmin || ['labels'].some(a => user?.roles.find(b => b.name === a)) || false,
      distributors: isAdmin || ['distributors'].some(a => user?.roles.find(b => b.name === a)) || false,
    };
  }, [user, isAdmin]);
  
  const userAccess: Access = useMemo(() => {
    const canQueueFullAlbum = resolveCanQueueFullAlbum(
      isAdmin,
      getRestrictionValue,
    );

    return {
      admin: {
        access: hasPermission('admin.access'),
      },
      backstage: {
        access: hasPermission('backstage.access'),
        insights: hasPermission('backstage.insights'),
      },
      medias: {
        play: {
          base: hasPermission('medias.play'),
          preview: getRestrictionValue('medias.play', 'preview'),
          highQuality: getRestrictionValue('medias.play', 'high_quality'),
          unencoded: getRestrictionValue('medias.play', 'unencoded'),
          unavailable: getRestrictionValue('medias.play', 'unavailable'),
          unreleased: getRestrictionValue('medias.play', 'unreleased'),
          listen24hMaxSeconds: getRestrictionValue('medias.play', 'listen_24h_max_seconds'),
          nextPerHour: getRestrictionValue('medias.play', 'next_per_hour'),
          prevPerHour: getRestrictionValue('medias.play', 'prev_per_hour'),
          canShuffle: getRestrictionValue('medias.play', 'can_shuffle'),
          canRepeat: getRestrictionValue('medias.play', 'can_repeat'),
          canSeek: getRestrictionValue('medias.play', 'can_seek'),
          queueAutoFill: getRestrictionValue('medias.play', 'queue_auto_fill'),
          canQueueFullAlbum,
          // Legacy alias kept for compatibility in callers not migrated yet.
          queueAutoMix: !canQueueFullAlbum,
        },
        view: {
          base: hasPermission('medias.view'),
          unencoded: getRestrictionValue('medias.view', 'unencoded'),
          unavailable: getRestrictionValue('medias.view', 'unavailable'),
          unreleased: getRestrictionValue('medias.view', 'unreleased'),
        },
        create: {
          music: !!getRestrictionValue('medias.create', 'music'),
          music_video: !!getRestrictionValue('medias.create', 'music_video'),
          podcast: !!getRestrictionValue('medias.create', 'podcast'),
          podcast_video: !!getRestrictionValue('medias.create', 'podcast_video'),
          audiobook: !!getRestrictionValue('medias.create', 'audiobook'),
        },
        update: hasPermission('medias.update'),
        delete: hasPermission('medias.delete'),
        download: hasPermission('medias.download'),
        upload: {
          audio: {
            basic: hasPermission('upload.audio.limited'),
            extended: hasPermission('upload.audio.extended'),
          },
          video: hasPermission('upload.video'),
          image: {
            basic: hasPermission('upload.image.basic'),
            extended: hasPermission('upload.image.extended'),
          },
        },
        embed: hasPermission('medias.embed'),
      },
      liveEvent: {
        view: {
          base: hasPermission('live_event.view'),
          unpublished: getRestrictionValue('live_event.view', 'unpublished'),
          unavailable: getRestrictionValue('live_event.view', 'unavailable'),
        },
        create: hasPermission('live_event.create'),
        update: hasPermission('live_event.update'),
        delete: hasPermission('live_event.delete'),
        tickets: {
          view: {
            base: hasPermission('live_event.tickets.view'),
            unpublished: getRestrictionValue('live_event.tickets.view', 'unpublished'),
            unavailable: getRestrictionValue('live_event.tickets.view', 'unavailable'),
          },
          create: hasPermission('live_event.tickets.create'),
          update: hasPermission('live_event.tickets.update'),
          delete: hasPermission('live_event.tickets.delete'),
          sell: hasPermission('live_event.tickets.sell'),
        },
        orders: {
          view: hasPermission('live_event.orders.view'),
          create: hasPermission('live_event.orders.create'),
          update: hasPermission('live_event.orders.update'),
          delete: hasPermission('live_event.orders.delete'),
          orders: {
            view: hasPermission('live_event.orders.items.view'),
            create: hasPermission('live_event.orders.items.create'),
            update: hasPermission('live_event.orders.items.update'),
            delete: hasPermission('live_event.orders.items.delete'),
          }
        },
        entryRight: {
          view: hasPermission('live_event.entry_right.view'),
          create: hasPermission('live_event.entry_right.create'),
          update: hasPermission('live_event.entry_right.update'),
          delete: hasPermission('live_event.entry_right.delete'),
          scan: hasPermission('live_event.entry_right.scan'),
        },
        scanSession: {
          view: hasPermission('live_event.scan_sess.view'),
          create: hasPermission('live_event.scan_sess.create'),
          update: hasPermission('live_event.scan_sess.update'),
          delete: hasPermission('live_event.scan_sess.delete'),
        },
      },
      liveVote: {
        view: hasPermission('live_vote.view'),
        create: hasPermission('live_vote.create'),
        update: hasPermission('live_vote.update'),
        delete: hasPermission('live_vote.delete'),
        launch_server: hasPermission('live_vote.launch_server'),
      }
    };
  }, [user, hasPermission, getRestrictionValue]);
  
  return { isAdmin, userRoles, userAccess };
}

export function getUserRolesAccess() {
  const data = getBootstrapData(); // Récupère les données utilisateur dynamiquement
  const user = data?.user;
  const isAdmin = user?.permissions?.some(p => p.name === 'admin');

  function hasPermission(name: string): boolean {
    return isAdmin || getPermission(name) != null;
  }
  
  function getPermission(name: string): Permission | undefined {
    const permissions = user?.permissions || data.guest_role?.permissions;
    if (!permissions) return;
    return permissions.find(p => p.name === name);
  }

  function getRestrictionValue(
    permissionName: string,
    restrictionName: string,
  ): string | number | boolean | undefined | null {
    const permission = getPermission(permissionName);
    let restrictionValue = null;
    let restrictionType: string | undefined;
    if (permission) {
      const restriction = permission.restrictions.find(
        r => r.name === restrictionName,
      );
      restrictionValue = restriction ? restriction.value : undefined;
      restrictionType = restriction?.type;
    }
    if (isAdmin) {
      const isNumericRestriction =
        restrictionType === 'number' || typeof restrictionValue === 'number';
      if (isNumericRestriction) {
        return null;
      }
      return true;
    }
    return restrictionValue;
  }

  const userRoles: Roles = {
    admins: isAdmin || ['admins'].some(a => user?.roles.find(b => b.name === a)) || false,
    users: isAdmin || ['users'].some(a => user?.roles.find(b => b.name === a)) || false,
    artists: isAdmin || ['artists'].some(a => user?.roles.find(b => b.name === a)) || false,
    creators: isAdmin || ['creators'].some(a => user?.roles.find(b => b.name === a)) || false,
    authors: isAdmin || ['authors'].some(a => user?.roles.find(b => b.name === a)) || false,
    labels: isAdmin || ['labels'].some(a => user?.roles.find(b => b.name === a)) || false,
    distributors: isAdmin || ['distributors'].some(a => user?.roles.find(b => b.name === a)) || false,
  };

  const canQueueFullAlbum = resolveCanQueueFullAlbum(
    Boolean(isAdmin),
    getRestrictionValue,
  );
  
  const userAccess: Access = {
    admin: {
      access: hasPermission('admin.access'),
    },
    backstage: {
      access: hasPermission('backstage.access'),
      insights: hasPermission('backstage.insights'),
    },
    medias: {
      play: {
        base: hasPermission('medias.play'),
        preview: getRestrictionValue('medias.play', 'preview'),
        highQuality: getRestrictionValue('medias.play', 'high_quality'),
        unencoded: getRestrictionValue('medias.play', 'unencoded'),
        unavailable: getRestrictionValue('medias.play', 'unavailable'),
        unreleased: getRestrictionValue('medias.play', 'unreleased'),
        listen24hMaxSeconds: getRestrictionValue('medias.play', 'listen_24h_max_seconds'),
        nextPerHour: getRestrictionValue('medias.play', 'next_per_hour'),
        prevPerHour: getRestrictionValue('medias.play', 'prev_per_hour'),
        canShuffle: getRestrictionValue('medias.play', 'can_shuffle'),
        canRepeat: getRestrictionValue('medias.play', 'can_repeat'),
        canSeek: getRestrictionValue('medias.play', 'can_seek'),
        queueAutoFill: getRestrictionValue('medias.play', 'queue_auto_fill'),
        canQueueFullAlbum,
        // Legacy alias kept for compatibility in callers not migrated yet.
        queueAutoMix: !canQueueFullAlbum,
      },
      view: {
        base: hasPermission('medias.view'),
        unencoded: getRestrictionValue('medias.view', 'unencoded'),
        unavailable: getRestrictionValue('medias.view', 'unavailable'),
        unreleased: getRestrictionValue('medias.view', 'unreleased'),
      },
      create: {
        music: !!getRestrictionValue('medias.create', 'music'),
        music_video: !!getRestrictionValue('medias.create', 'music_video'),
        podcast: !!getRestrictionValue('medias.create', 'podcast'),
        podcast_video: !!getRestrictionValue('medias.create', 'podcast_video'),
        audiobook: !!getRestrictionValue('medias.create', 'audiobook'),
      },
      update: hasPermission('medias.update'),
      delete: hasPermission('medias.delete'),
      download: hasPermission('medias.download'),
      upload: {
        audio: {
          basic: hasPermission('upload.audio.limited'),
          extended: hasPermission('upload.audio.extended'),
        },
        video: hasPermission('upload.video'),
        image: {
          basic: hasPermission('upload.image.basic'),
          extended: hasPermission('upload.image.extended'),
        },
      },
      embed: hasPermission('medias.embed'),
    },
    liveEvent: {
      view: {
        base: hasPermission('live_event.view'),
        unpublished: getRestrictionValue('live_event.view', 'unpublished'),
        unavailable: getRestrictionValue('live_event.view', 'unavailable'),
      },
      create: hasPermission('live_event.create'),
      update: hasPermission('live_event.update'),
      delete: hasPermission('live_event.delete'),
      tickets: {
        view: {
          base: hasPermission('live_event.tickets.view'),
          unpublished: getRestrictionValue('live_event.tickets.view', 'unpublished'),
          unavailable: getRestrictionValue('live_event.tickets.view', 'unavailable'),
        },
        create: hasPermission('live_event.tickets.create'),
        update: hasPermission('live_event.tickets.update'),
        delete: hasPermission('live_event.tickets.delete'),
        sell: hasPermission('live_event.tickets.sell'),
      },
      orders: {
        view: hasPermission('live_event.orders.view'),
        create: hasPermission('live_event.orders.create'),
        update: hasPermission('live_event.orders.update'),
        delete: hasPermission('live_event.orders.delete'),
        orders: {
          view: hasPermission('live_event.orders.items.view'),
          create: hasPermission('live_event.orders.items.create'),
          update: hasPermission('live_event.orders.items.update'),
          delete: hasPermission('live_event.orders.items.delete'),
          scan: hasPermission('live_event.orders.items.scan'),
        }
      },
      entryRight: {
        view: hasPermission('live_event.entry_right.view'),
        create: hasPermission('live_event.entry_right.create'),
        update: hasPermission('live_event.entry_right.update'),
        delete: hasPermission('live_event.entry_right.delete'),
        scan: hasPermission('live_event.entry_right.scan'),
      },
      scanSession: {
        view: hasPermission('live_event.scan_sess.view'),
        create: hasPermission('live_event.scan_sess.create'),
        update: hasPermission('live_event.scan_sess.update'),
        delete: hasPermission('live_event.scan_sess.delete'),
      },
    },
    liveVote: {
      view: hasPermission('live_vote.view'),
      create: hasPermission('live_vote.create'),
      update: hasPermission('live_vote.update'),
      delete: hasPermission('live_vote.delete'),
      launch_server: hasPermission('live_vote.launch_server'),
    }
  };

  return { isAdmin, userRoles, userAccess };
}
